// Mobile-Optimized GamePredictionModal.js with Enhanced Star Coins Integration
// and GameResultsModal for displaying and saving predictions
import { getAuth } from 'firebase/auth';
import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, BarChart2, Newspaper, Brain, Target, Loader2, X, Clock, Star, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUsageLimiter } from '../../utils/usageLimiter';
import StarCoinsModal from '../starCoinSystem/StarCoinsModal';
import GameResultsModal from './GameResultsModal';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { gamePredictionModalPtBR } from '../mappings/gamePredictionModal.pt-br';

// Import sport-specific components
import {
  MLBPredictionComponent,
  NBAPredictionComponent,
  NHLPredictionComponent,
  NFLPredictionComponent,
  NCAAFPredictionComponent,
  SoccerPredictionComponent,
  NCAABaseballPredictionComponent,
  NBAFinalsPredictionComponent,
  MLSPredictionComponent,
  BrasileirãoPredictionComponent,
  SportSelectionComponent
} from './SportPredictionComponents';

const GamePredictionModal = ({ 
  predictionState, 
  onSportSelect, 
  onClose, 
  onUpgrade, 
  isBasicUser,
  remainingSpins,
  resetTime,
  userId
}) => {
  const navigate = useNavigate();
  const [showResetInfo, setShowResetInfo] = useState(false);
  const [showStarCoinsModal, setShowStarCoinsModal] = useState(false);
  const [transactionInProgress, setTransactionInProgress] = useState(false);
  const [transactionError, setTransactionError] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  
  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();
  
  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return gamePredictionModalPtBR[text] || text;
  };
  
  // Get Star Coins data from usageLimiter
  const { starCoins = {} } = useUsageLimiter(userId);
  
  // Star Coins cost for game prediction
  const PREDICTION_COST = 7; // Cost in Star Coins for one game prediction
  
  // Format reset time for display
  const formattedResetTime = useMemo(() => {
    if (!resetTime) return null;
    
    try {
      const resetDate = new Date(resetTime);
      return resetDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      console.error('Error formatting reset time:', e);
      return null;
    }
  }, [resetTime]);

  // Format relative reset time (e.g. "in 2 hours")
  const relativeResetTime = useMemo(() => {
    if (!resetTime) return null;
    
    try {
      const now = new Date();
      const resetDate = new Date(resetTime);
      const diffMs = Math.max(0, resetDate - now);
      
      // If less than a minute, show "Soon"
      if (diffMs < 60000) return t("Soon");
      
      const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffHrs > 0) {
        return `${t('in')} ${diffHrs}${t('h')} ${diffMins}${t('m')}`;
      } else {
        return `${t('in')} ${diffMins}${t('m')}`;
      }
    } catch (e) {
      console.error('Error formatting relative reset time:', e);
      return "soon";
    }
  }, [resetTime]);
  
  // Reset transaction state when modal closes
  useEffect(() => {
    if (!predictionState.showModal) {
      setTransactionError(null);
      setTransactionInProgress(false);
      setShowResultsModal(false);
    }
  }, [predictionState.showModal]);
  
  // Refresh Star Coins balance when the modal is opened
  useEffect(() => {
    if (predictionState.showModal && userId) {
      starCoins.loadBalance().catch(err => {
        console.error('Error loading Star Coins balance:', err);
      });
    }
  }, [predictionState.showModal, userId, starCoins]);

  // Show results modal when prediction is complete
  useEffect(() => {
    if (predictionState.results && predictionState.step === 3) {
      setShowResultsModal(true);
    }
  }, [predictionState.results, predictionState.step]);
  
  const steps = useMemo(() => [
    { 
      icon: Trophy, 
      text: predictionState.selectedSport ? `${t('Finding Best')} ${t(predictionState.selectedSport)} ${t('Game')}` : t("Select Sport"),
      microsteps: [
        t("Scanning today's matchups..."),
        t("Analyzing opening lines..."),
        t("Identifying value opportunities..."),
        t("Ranking game potential...")
      ]
    },
    { 
      icon: Brain, 
      text: t("Generating Comprehensive Prediction"),
      microsteps: [
        t("Gathering latest news and stats..."),
        t("Processing team and player data..."),
        t("Building prediction model..."),
        t("Running simulations...")
      ]
    },
    { 
      icon: Target, 
      text: t("Finalizing Analysis"),
      microsteps: [
        t("Calculating win probabilities..."),
        t("Determining confidence level..."),
        t("Finalizing recommendation..."),
        t("Preparing detailed report...")
      ]
    }
  ], [predictionState.selectedSport, t]);
  
  
  const availableSports = useMemo(() => [
    { name: "MLB", icon: "⚾", emoji: true, inSeason: true },
    { name: "NFL", icon: "🏈", emoji: true, inSeason: true },
    // { name: "NCAAF", icon: "🏈", emoji: true, inSeason: true }, // Temporarily disabled
    { name: "Soccer", icon: "⚽", emoji: true, inSeason: true },
    { name: "MLS", icon: "⚽", emoji: true, inSeason: true },
    { name: "Brasileirão", icon: "⚽", emoji: true, inSeason: true },
    { name: "NBA", icon: "🏀", emoji: true, inSeason: false },
    { name: "NHL", icon: "🏒", emoji: true, inSeason: false },
    { name: "NCAA", icon: "⚾", emoji: true, inSeason: false },
    { name: "NBA Finals", icon: "🏆", emoji: true, inSeason: false }
  ], []);
  
  const displayStep = predictionState.step === 0 ? 0 : Math.min(predictionState.step, steps.length - 1);

  // Handle using Star Coins for prediction
  const handleUseStarCoins = async () => {
    // Reset error state
    setTransactionError(null);
    
    // Check if we have enough Star Coins
    if ((starCoins.balance || 0) < PREDICTION_COST) {
      // Not enough coins, show purchase modal
      setShowStarCoinsModal(true);
      return;
    }
    
    try {
      setTransactionInProgress(true);
      
      // Consume Star Coins
      const result = await starCoins.consumeStarCoins(PREDICTION_COST, 'gamePrediction');
      
      if (result.success) {
        // Star Coins consumed successfully
        
        // Refresh Star Coins balance
        await starCoins.loadBalance();
        
        // Show sport selector
        if (predictionState.selectedSport) {
          // If sport is already selected, just run the prediction
          if (onSportSelect) {
            onSportSelect(predictionState.selectedSport);
          }
        } else {
          // Just show the sport selector - we don't have direct access to updatePredictionState
          // Instead, use the structure already in the component where predictionState.showSportSelector is controlled
          predictionState.showSportSelector = true;
          predictionState.paidWithStarCoins = true; // Add this flag to track payment method
          
          // Since we're modifying the object directly, notify the component to update
          // This will be handled by the component's onSportSelect prop
          if (onSportSelect) {
            // Call it with null to signal we just want to update state without selecting a sport
            onSportSelect(null, { showSportSelector: true, paidWithStarCoins: true });
          }
        }
      } else {
        // Error consuming Star Coins
        setTransactionError(result.error || "Failed to use Star Coins. Please try again.");
        
        // If the error is related to insufficient balance, show the purchase modal
        if (result.error && result.error.includes("insufficient")) {
          setShowStarCoinsModal(true);
        }
      }
    } catch (error) {
      console.error('Error using Star Coins for prediction:', error);
      setTransactionError("An unexpected error occurred. Please try again.");
    } finally {
      setTransactionInProgress(false);
    }
  };

  // Handle Star Coins modal close
  const handleStarCoinsModalClose = async () => {
    setShowStarCoinsModal(false);
    
    // Refresh Star Coins balance when modal closes
    try {
      await starCoins.loadBalance();
    } catch (err) {
      console.error('Error refreshing Star Coins balance:', err);
    }
  };

  // Handle close of the results modal
  const handleResultsModalClose = () => {
    setShowResultsModal(false);
    // Also close the parent GamePredictionModal when results modal closes
    if (onClose) {
      onClose();
    }
  };

  // Limit reached popup for pro users - Enhanced with Star Coins (Mobile Optimized)
  const LimitReachedPopup = () => (
    <motion.div
      key="limit-popup-overlay"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-3 sm:p-4"
    >
      <div className="bg-black/90 backdrop-blur-lg border border-white/10 rounded-lg p-4 sm:p-5 max-w-sm sm:max-w-md w-full relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={() => setShowResetInfo(false)}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 sm:p-1 text-white/40 hover:text-white/80 transition-colors min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center justify-center mb-4 mt-6 sm:mt-0">
          <div className="h-10 w-10 sm:h-12 sm:w-12 bg-white/[0.05] rounded-full flex items-center justify-center">
            <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-white/50" />
          </div>
        </div>

        <h3 className="text-xs sm:text-sm font-medium tracking-[0.2em] text-white/30 uppercase text-center mb-2">{t('Spin Limit Reached')}</h3>

        <p className="text-sm sm:text-base text-white/70 text-center mb-4">
          {isBasicUser
            ? t("You've used your 1 free AI prediction for today.")
            : t("You've used all 7 of your AI prediction spins for now.")}
        </p>

        {formattedResetTime && (
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-3 text-center mb-4">
            <p className="text-xs sm:text-sm text-white/50 mb-1 uppercase tracking-wider">{t('Your spins will reset at:')}</p>
            <p className="text-base sm:text-lg font-mono text-emerald-400/80">{formattedResetTime}</p>
            <p className="text-xs text-white/30 mt-1 font-mono">
              ({isBasicUser ? t("24 hours from last use") : t("2 hours from last use")})
            </p>
          </div>
        )}

        {/* Star Coins Option - Mobile Optimized */}
        <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-3 sm:p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400/70" />
              <span className="text-xs sm:text-sm text-white/70 uppercase tracking-wider">{t('Use Star Coins')}</span>
            </div>
            <div className="text-[11px] sm:text-xs text-white/30 font-mono">
              {t('Balance')}: <span className="text-violet-400/70">{starCoins.balance || 0}</span>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-white/50 mb-3">
            {t('Run another prediction for')} <span className="font-mono text-violet-400/70">{PREDICTION_COST}</span> {t('Star Coins')}
          </p>
          <button
            onClick={handleUseStarCoins}
            disabled={transactionInProgress}
            className={`w-full py-3 sm:py-2 rounded-md flex items-center justify-center text-xs sm:text-sm min-h-[44px] transition-all duration-200 ${
              (starCoins.balance || 0) >= PREDICTION_COST
                ? 'bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 text-white uppercase tracking-wider'
                : 'bg-white/[0.02] border border-white/[0.05] text-white/40 uppercase tracking-wider'
            } ${transactionInProgress ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {transactionInProgress
              ? t('Processing...')
              : (starCoins.balance || 0) >= PREDICTION_COST
                ? t('Continue with Star Coins')
                : t('Buy Star Coins')}
          </button>

          {/* Transaction error message */}
          {transactionError && (
            <div className="mt-2 text-xs sm:text-sm text-red-400/70 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" />
              <span>{transactionError}</span>
            </div>
          )}
        </div>

        {isBasicUser && (
          <div className="bg-white/[0.02] backdrop-blur-sm border border-emerald-500/20 rounded-lg p-3 sm:p-4 mb-4">
            <div className="text-xs sm:text-sm text-white/50 uppercase tracking-wider mb-2">{t('Pro Upgrade Benefits:')}</div>
            <ul className="text-xs sm:text-sm text-white/50 space-y-1 mb-3">
              <li>• {t('7 free predictions every 3 hours')}</li>
              <li>• {t('Priority access to premium features')}</li>
              <li>• {t('Advanced statistics and analysis')}</li>
            </ul>
            <button
              onClick={() => {
                setShowResetInfo(false);
                onUpgrade();
              }}
              className="w-full py-3 sm:py-2 rounded-md bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 text-white text-xs sm:text-sm uppercase tracking-wider min-h-[44px] transition-all duration-200"
            >
              {t('Upgrade to Pro')}
            </button>
          </div>
        )}

        <button
          onClick={() => setShowResetInfo(false)}
          className="w-full py-3 sm:py-2.5 bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.03] text-white/70 hover:text-white/80 rounded-md text-xs sm:text-sm uppercase tracking-wider min-h-[44px] transition-all duration-200"
        >
          {t('Got it')}
        </button>
      </div>
    </motion.div>
  );
  
  // Helper function to render the appropriate sport component
  const renderSportComponent = () => {
    if (!predictionState.selectedSport) {
      return null;
    }
    
    // Common props for all sport components
    const commonProps = {
      predictionState,
      displayStep,
      steps,
      remainingSpins,
      isBasicUser,
      onClose,
      navigate,
      setShowResetInfo
    };

    // Switch based on selected sport
    switch (predictionState.selectedSport) {
      case 'MLB':
        return <MLBPredictionComponent {...commonProps} />;
      case 'NBA':
        return <NBAPredictionComponent {...commonProps} />;
      case 'NHL':
        return <NHLPredictionComponent {...commonProps} />;
      case 'NFL':
        return <NFLPredictionComponent {...commonProps} />;
      case 'NCAAF':
        return <NCAAFPredictionComponent {...commonProps} />;
      case 'Soccer':
        return <SoccerPredictionComponent {...commonProps} />;
      case 'NCAA Baseball':
        return <NCAABaseballPredictionComponent {...commonProps} />;
      case 'NBA Finals':
        return <NBAFinalsPredictionComponent {...commonProps} />;
      case 'MLS':
        return <MLSPredictionComponent {...commonProps} />;
      case 'Brasileirão':
        return <BrasileirãoPredictionComponent {...commonProps} />;
      default:
        // Fallback to MLB if for some reason the sport isn't matched
        return <MLBPredictionComponent {...commonProps} />;
    }
  };
  
  return (
    <>
      <AnimatePresence mode="wait">
        {predictionState.showModal && !showResultsModal && (
          <motion.div
            key="prediction-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-3 sm:p-4"
            onClick={() => {
              if (!predictionState.isRunning && !predictionState.showSportSelector) {
                onClose();
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-black/90 backdrop-blur-lg border border-white/10 rounded-lg shadow-2xl max-w-xs sm:max-w-lg w-full max-h-[50vh] sm:max-h-[85vh] relative flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Sticky header with close button */}
              <div className="sticky top-0 z-10 bg-black/90 backdrop-blur-lg rounded-t-lg border-b border-white/[0.05] p-3 sm:p-6 pb-3 sm:pb-4">
                <button
                  onClick={onClose}
                  className="absolute top-2 right-2 sm:top-4 sm:right-4 p-1.5 sm:p-2 text-white/40 hover:text-white/80 transition-colors duration-200"
                  aria-label="Close prediction modal"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>

                {/* Modal title - part of sticky header */}
                <div className="text-center pr-10 sm:pr-12">
                  <h3 className="text-xs font-medium tracking-[0.3em] text-white/30 uppercase mb-1 sm:mb-2">
                    {t('AI Game Prediction')}
                  </h3>
                  {!predictionState.showSportSelector && (
                    <p className="text-xs sm:text-sm text-white/50">
                      {t('Building your comprehensive analysis...')}
                    </p>
                  )}
                </div>
              </div>

              {/* Scrollable content area */}
              <div className="overflow-y-auto flex-1 p-3 sm:p-6 pt-2">
                {/* Remaining spins indicator with better description */}
                <div className="text-center mb-3 sm:mb-4">
                  {isBasicUser ? (
                    <div>
                      <div className="font-mono text-xs sm:text-sm text-white/50">
                        {remainingSpins > 0 ? (
                          <span>
                            <span className="text-emerald-400/80">{remainingSpins}</span> {t('free spin remaining today')}
                          </span>
                        ) : (
                          t('Daily free spin used')
                        )}
                      </div>
                      {remainingSpins > 0 && (
                        <p className="text-[10px] sm:text-xs text-white/30 mt-1">
                          {t('Use AI to analyze today\'s games and get expert betting predictions')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div className="font-mono text-xs sm:text-sm text-white/50">
                        {remainingSpins > 0 ? (
                          <span>
                            <span className="text-emerald-400/80">{remainingSpins}</span> {remainingSpins !== 1 ? t('spins') : t('spin')} {t('remaining')} • {t('Resets')} {relativeResetTime || t('soon')}
                          </span>
                        ) : (
                          <button
                            onClick={() => setShowResetInfo(true)}
                            className="text-white/40 hover:text-emerald-400/70 uppercase tracking-wider transition-colors duration-200 text-[11px] min-h-[44px] py-2 px-1"
                          >
                            {t('Spin limit reached')} →
                          </button>
                        )}
                      </div>
                      {remainingSpins > 0 && (
                        <p className="text-[10px] sm:text-xs text-white/30 mt-1">
                          {t('Select a sport to get AI-powered predictions for today\'s best bets')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                {predictionState.showSportSelector ? (
                <SportSelectionComponent 
                  availableSports={availableSports}
                  onSportSelect={onSportSelect}
                  remainingSpins={remainingSpins}
                  predictionState={predictionState}
                />
              ) : predictionState.step < 3 ? (
                renderSportComponent()
              ) : (
                <div className="flex items-center justify-center h-32 sm:h-40">
                  <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-300 animate-spin" />
                </div>
              )}
              
              {/* Zero spins remaining - Enhanced with Star Coins option (Mobile Optimized) */}
              {remainingSpins === 0 && !predictionState.results && (
                <div className="mt-6 sm:mt-8 space-y-4">
                  {/* Star Coins Option */}
                  <div className="bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-lg p-3 sm:p-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 sm:w-5 sm:h-5 text-violet-400/70" />
                        <span className="text-xs sm:text-sm text-white/70 uppercase tracking-wider">{t('Use Star Coins')}</span>
                      </div>
                      <div className="text-[11px] sm:text-xs text-white/30 font-mono">
                        {t('Balance')}: <span className="text-violet-400/70">{starCoins.balance || 0}</span>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-white/50 mb-3">
                      {t('Run a prediction for')} <span className="font-mono text-violet-400/70">{PREDICTION_COST}</span> {t('Star Coins')}
                    </p>
                    <button
                      onClick={handleUseStarCoins}
                      disabled={transactionInProgress}
                      className={`w-full py-3 sm:py-2 rounded-md flex items-center justify-center text-xs sm:text-sm min-h-[44px] transition-all duration-200 ${
                        (starCoins.balance || 0) >= PREDICTION_COST
                          ? 'bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 text-white uppercase tracking-wider'
                          : 'bg-white/[0.02] border border-white/[0.05] text-white/40 uppercase tracking-wider'
                      } ${transactionInProgress ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {transactionInProgress
                        ? t('Processing...')
                        : (starCoins.balance || 0) >= PREDICTION_COST
                          ? t('Continue with Star Coins')
                          : t('Buy Star Coins')}
                    </button>

                    {/* Transaction error message */}
                    {transactionError && (
                      <div className="mt-2 text-xs sm:text-sm text-red-400/70 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4" />
                        <span>{transactionError}</span>
                      </div>
                    )}
                  </div>

                  {/* Upgrade option for basic users */}
                  {isBasicUser && (
                    <div className="bg-white/[0.02] backdrop-blur-sm border border-emerald-500/20 rounded-lg p-3 sm:p-4 text-center">
                      <p className="text-white/50 text-xs sm:text-sm mb-3">
                        {t('Want more predictions without using Star Coins?')}
                      </p>
                      <button
                        onClick={onUpgrade}
                        className="px-4 sm:px-6 py-3 sm:py-2 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 hover:from-emerald-500 hover:to-violet-500 rounded-md text-white text-xs sm:text-sm uppercase tracking-wider min-h-[44px] w-full sm:w-auto transition-all duration-200"
                      >
                        {t('Upgrade to Pro')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              </div> {/* End of scrollable content */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Pro user limit reached info popup */}
      <AnimatePresence mode="wait">
        {showResetInfo && <LimitReachedPopup key="limit-reached-popup" />}
      </AnimatePresence>
      
      {/* Star Coins purchase modal */}
      <StarCoinsModal
        isOpen={showStarCoinsModal}
        onClose={handleStarCoinsModalClose}
        userId={userId}
      />

      {/* Game Results Modal */}
      <GameResultsModal
        isOpen={showResultsModal}
        onClose={handleResultsModalClose}
        predictionState={predictionState}
        remainingSpins={remainingSpins}
        isBasicUser={isBasicUser}
        resetTime={resetTime}
      />
    </>
  );
};

export default GamePredictionModal;